import { DateHelper } from "apps/idl/src/helpers";
import { IPatient } from "../../../../modules/patients/model/IPatient";
import { IPatientCV } from "../../../../modules/patients/model/IPatientCV";
import { ETimetablePattern } from "apps/idl/src/model";

export class PatientMatchingHelper {

  /**
   * Supprime accents, caractères spéciaux et met en majuscules pour comparer plus facilement.
   */
  private static normaliserTexte(texte: string): string {
    return texte
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z]/g, '')
      .toUpperCase();
  }

  /**
   * Calcule la distance de Damerau-Levenshtein entre deux chaînes.
   * Prend en compte insertions, suppressions, substitutions et transpositions.
   */
  private static calculerDistanceDamerauLevenshtein(chaine1: string, chaine2: string): number {
    const len1 = chaine1.length;
    const len2 = chaine2.length;
    const matrice = Array.from({ length: len1 + 1 }, () => new Array(len2 + 1).fill(0));

    for (let i = 0; i <= len1; i++) matrice[i][0] = i;
    for (let j = 0; j <= len2; j++) matrice[0][j] = j;

    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        const cout = chaine1[i - 1] === chaine2[j - 1] ? 0 : 1;

        matrice[i][j] = Math.min(
          matrice[i - 1][j] + 1,
          matrice[i][j - 1] + 1,
          matrice[i - 1][j - 1] + cout
        );

        if (
          i > 1 &&
          j > 1 &&
          chaine1[i - 1] === chaine2[j - 2] &&
          chaine1[i - 2] === chaine2[j - 1]
        ) {
          matrice[i][j] = Math.min(matrice[i][j], matrice[i - 2][j - 2] + cout);
        }
      }
    }

    return matrice[len1][len2];
  }

  /**
   * Retourne un score de similarité (0 à 1) entre deux chaînes.
   */
  private static calculerSimilarite(chaine1: string, chaine2: string): number {
    const normalise1 = this.normaliserTexte(chaine1);
    const normalise2 = this.normaliserTexte(chaine2);
    const distance = this.calculerDistanceDamerauLevenshtein(normalise1, normalise2);
    const longueurMax = Math.max(normalise1.length, normalise2.length) || 1;
    return 1 - distance / longueurMax;
  }

  /**
   * Vérifie si deux couples (nom, prénom) sont similaires,
   * y compris en cas d'inversion (ex: nom <-> prénom).
   */
  private static sontNomPrenomSimilaires(
    nomBase: string,
    prenomBase: string,
    nomCv: string,
    prenomCv: string,
    seuil: number = 0.8
  ): { similaire: boolean, scoreMax: number } {
    const base1 = (nomBase + prenomBase).toLowerCase().trim();
    const base2 = (prenomBase + nomBase).toLowerCase().trim();
    const cible = (nomCv + prenomCv).toLowerCase().trim();

    const score1 = this.calculerSimilarite(base1, cible);
    const score2 = this.calculerSimilarite(base2, cible);

    const scoreMax = Math.max(score1, score2);
    const similaire = scoreMax >= seuil;

    return {
      similaire,
      scoreMax
    };
  }

  /**
   * Calcule un score de correspondance (1 à 6) entre deux patients.
   * 1 = correspondance forte, 6 = faible.
   */
  private static calculerPertinence(patient: IPatient, cv: IPatientCV): { pertinence: number, distance: number } {
    const nirMatch = cv.socialNumber === patient.socialNumber;

    const dnPatient = patient.lunarDate ?? patient.birthDate;
    const dnPatientCV = cv.lunarDate ?? cv.birthDate;
    const dnMatch = DateHelper.transform(dnPatient, ETimetablePattern.dd_MM_yyyy) ===
      DateHelper.transform(dnPatientCV, ETimetablePattern.dd_MM_yyyy);

    const nom = patient.lastName || '';
    const prenom = patient.firstName || '';
    const nomCv = cv.lastName || cv.usualLastName || '';
    const prenomCv = cv.firstName || '';

    const nomPrenomMatch = this.sontNomPrenomSimilaires(nom, prenom, nomCv, prenomCv);

    const nbMatch = [nirMatch, nomPrenomMatch.similaire, dnMatch].filter(Boolean).length;

    let pertinence: number;

    if (nbMatch === 3) {
      pertinence = 1;
    } else if (nbMatch === 2) {
      if (nirMatch && dnMatch) pertinence = 2;
      else if (nirMatch && nomPrenomMatch.similaire) pertinence = 3;
      else if (dnMatch && nomPrenomMatch.similaire) pertinence = 4;
      else pertinence = 6;
    } else if (nbMatch === 1) {
      pertinence = nirMatch ? 5 : 6;
    } else {
      pertinence = 6;
    }

    return {
      pertinence,
      distance: nomPrenomMatch.scoreMax 
    };
  }


  /**
   * Calcule la pertinence entre un CV et une liste de patients.
   * Retourne une Map de patient ID → score.
   */
  public static calculerPertinencePourListe(patients: IPatient[], cv: IPatientCV): Map<string, {pertinence: number, distance: number}> {
    const result = new Map<string, { pertinence: number, distance: number }>();
    for (const patient of patients) {
      const resultPatient = this.calculerPertinence(patient, cv);
      result.set(patient._id, resultPatient);
    }
    return result;
  }
}
